import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { PromoTypeFields } from '@/components/common/PromoTypeFields';
import { PromoSellerPicker } from '@/components/common/PromoSellerPicker';
import { StockHistoryDialog } from '@/components/common/StockHistoryDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PinVerificationDialog } from '@/components/common/PinVerificationDialog';
import { CategoryCombobox } from '@/components/common/CategoryCombobox';
import { Plus, Pencil, Trash, Image as ImageIcon, X, Download, Upload, Tag, MagnifyingGlass, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';
import { compressImage } from '@/lib/imageCompress';
import { MissingInfoBadges } from '@/components/common/MissingInfoBadges';
import { ProfitStatsDialog } from '@/components/common/ProfitStatsDialog';
import { DormantProductsDialog } from '@/components/common/DormantProductsDialog';
import { ProfitRankingDialog } from '@/components/common/ProfitRankingDialog';
import { BulkCostDialog } from '@/components/common/BulkCostDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Products = ({ embedded = false, embedEditId = null, onEmbedClose } = {}) => {
  const { products, packages, categories, sellers, refreshProducts, refreshCategories } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showVariants, setShowVariants] = useState({});
  const [importExportLoading, setImportExportLoading] = useState(false);
  const [pendingApprovalId, setPendingApprovalId] = useState(null);

  // Polling de aprobación de cambio de producto
  useEffect(() => {
    if (!pendingApprovalId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/approvals/${pendingApprovalId}`, { withCredentials: true });
        if (data.status === 'approved') {
          toast.success('✅ Cambios aprobados y aplicados');
          setPendingApprovalId(null);
          // Pequeño delay para asegurar que el webhook terminó de insertar
          // (status se marca aprobado antes de aplicar los cambios)
          setTimeout(() => { refreshProducts(); }, 500);
        } else if (data.status === 'rejected') {
          toast.error('❌ Cambios rechazados por el admin');
          setPendingApprovalId(null);
        }
      } catch (e) { /* ignore */ }
    }, 2500);
    return () => clearInterval(interval);
  }, [pendingApprovalId, refreshProducts]);
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    precio_1: '',
    precio_2: '',
    precio_3: '',
    precio_4: '',
    precio_5: '',
    mayoreo: '',
    medio_mayoreo: '',
    stock: '0',
    min_stock: '10',
    default_commission: '0',
    image_url: '',
    extra_images: [],
    has_variants: false,
    category_id: 'none',
    brand: '',
    has_promo: false,
    promo_type: 'flat',
    promo_price: '',
    promo_min_qty: '',
    promo_end_date: '',
    promo_local_only: false,
    promo_shipment_only: false,
    promo_discount_percent: '',
    promo_qty_limit: '',
    promo_gift_product_id: '',
    promo_gift_qty: 1,
    promo_seller_restricted: false,
    promo_allowed_seller_ids: [],
    visible_in_pos: true,
    visible_in_catalog: true,
    is_new_arrival: false,
    product_info: '',
    purchase_cost: ''
  });
  const [promoDialog, setPromoDialog] = useState(false);
  
  const [variants, setVariants] = useState([]);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [currentVariant, setCurrentVariant] = useState({
    variant_name: '',
    sku: '',
    precio_1: '',
    precio_2: '',
    precio_3: '',
    precio_4: '',
    precio_5: '',
    mayoreo: '',
    medio_mayoreo: '',
    stock: '0',
    default_commission: '0',
    image_url: '',
    extra_images: []
  });

  // Sincronizar precios de currentVariant con formData
  useEffect(() => {
    if (!currentVariant.variant_name && formData.precio_1) {
      setCurrentVariant(prev => ({
        ...prev,
        precio_1: formData.precio_1,
        precio_2: formData.precio_2,
        precio_3: formData.precio_3,
        precio_4: formData.precio_4,
        precio_5: formData.precio_5,
        mayoreo: formData.mayoreo,
        medio_mayoreo: formData.medio_mayoreo,
        default_commission: formData.default_commission
      }));
    }
  }, [formData.precio_1, formData.precio_2, formData.precio_3, formData.precio_4, formData.precio_5, formData.mayoreo, formData.medio_mayoreo, formData.default_commission]);

  const fetchProducts = async () => {
    await Promise.all([refreshProducts(), refreshCategories()]);
  };

  // Al montar la página, forzar refresh de productos y categorías (para ver stock actualizado
  // tras ventas, transferencias, compras, etc. ocurridas en otra pestaña o sesión).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProducts(); }, []);

  const handleExport = async () => {
    setImportExportLoading(true);
    try {
      const response = await axios.get(`${API}/products/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `productos_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archivo exportado');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setImportExportLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm(`¿Importar productos desde "${file.name}"? Los productos existentes con el mismo SKU se actualizarán.`)) {
      e.target.value = '';
      return;
    }
    setImportExportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/products/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const msg = `Creados: ${data.created}, Actualizados: ${data.updated}` +
        (data.errors?.length ? `. Errores: ${data.errors.length}` : '');
      if (data.errors?.length) {
        toast.error(msg + '\n' + data.errors.slice(0, 3).join('\n'), { duration: 8000 });
      } else {
        toast.success(msg);
      }
      await fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImportExportLoading(false);
      e.target.value = '';
    }
  };

  const handleImageUpload = async (e, isVariant = false, variantIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      if (isVariant && variantIndex !== null) {
        const newVariants = [...variants];
        newVariants[variantIndex].image_url = dataUrl;
        setVariants(newVariants);
      } else if (isVariant) {
        setCurrentVariant({ ...currentVariant, image_url: dataUrl });
      } else {
        setFormData({ ...formData, image_url: dataUrl });
      }
    } catch (err) {
      toast.error(err.message || 'No se pudo procesar la imagen');
    } finally {
      e.target.value = '';
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      // El listing excluye `extra_images` para no cargar imágenes pesadas.
      // Cuando abrimos el modal para editar, pedimos el documento completo
      // para poder ver, sustituir o eliminar las imágenes adicionales.
      axios.get(`${API}/products/full/${product.id}`, { withCredentials: true })
        .then(({ data }) => {
          if (data?.extra_images && data.extra_images.length > 0) {
            setFormData(prev => ({ ...prev, extra_images: data.extra_images }));
          }
        })
        .catch(() => { /* ignore, editará sin las extras */ });
      setFormData({
        name: product.name,
        sku: product.sku,
        precio_1: product.precio_1,
        precio_2: product.precio_2,
        precio_3: product.precio_3,
        precio_4: product.precio_4,
        precio_5: product.precio_5,
        mayoreo: product.mayoreo,
        medio_mayoreo: product.medio_mayoreo,
        stock: product.stock,
        min_stock: product.min_stock || 10,
        default_commission: product.default_commission || 0,
        image_url: product.image_url || '',
        extra_images: product.extra_images || [],
        has_variants: product.has_variants || false,
        category_id: product.category_id || 'none',
        brand: product.brand || '',
        has_promo: product.has_promo || false,
        promo_type: product.promo_type || 'flat',
        promo_price: product.promo_price ?? '',
        promo_min_qty: product.promo_min_qty ?? '',
        promo_end_date: product.promo_end_date || '',
        promo_local_only: !!product.promo_local_only,
        promo_shipment_only: !!product.promo_shipment_only,
        promo_discount_percent: product.promo_discount_percent ?? '',
        promo_qty_limit: product.promo_qty_limit ?? '',
        promo_gift_product_id: product.promo_gift_product_id || '',
        promo_gift_qty: product.promo_gift_qty ?? 1,
        promo_combo_product_id: product.promo_combo_product_id || '',
        promo_combo_discount: product.promo_combo_discount ?? '',
        promo_description: product.promo_description || '',
        promo_seller_restricted: !!product.promo_seller_restricted,
        promo_allowed_seller_ids: product.promo_allowed_seller_ids || [],
        visible_in_pos: product.visible_in_pos !== false,
        visible_in_catalog: product.visible_in_catalog !== false,
        is_new_arrival: !!product.is_new_arrival,
        product_info: product.product_info || '',
        purchase_cost: product.purchase_cost ?? ''
      });
      
      // Load variants if exists
      if (product.has_variants) {
        const productVariants = products.filter(p => p.parent_product_id === product.id);
        setVariants(productVariants.map(v => ({
          id: v.id,
          variant_name: v.variant_name,
          sku: v.sku,
          precio_1: v.precio_1,
          precio_2: v.precio_2,
          precio_3: v.precio_3,
          precio_4: v.precio_4,
          precio_5: v.precio_5,
          mayoreo: v.mayoreo,
          medio_mayoreo: v.medio_mayoreo,
          stock: v.stock,
          default_commission: v.default_commission || 0,
          image_url: v.image_url || '',
          extra_images: v.extra_images || []
        })));
      } else {
        setVariants([]);
      }
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        sku: '',
        precio_1: '',
        precio_2: '',
        precio_3: '',
        precio_4: '',
        precio_5: '',
        mayoreo: '',
        medio_mayoreo: '',
        stock: '0',
        min_stock: '10',
        default_commission: '0',
        image_url: '',
        extra_images: [],
        has_variants: false,
        category_id: 'none',
        brand: '',
        has_promo: false,
        promo_type: 'flat',
        promo_price: '',
        promo_min_qty: '',
        promo_end_date: '',
        promo_local_only: false,
        promo_shipment_only: false,
        promo_discount_percent: '',
        promo_qty_limit: '',
        promo_gift_product_id: '',
        promo_gift_qty: 1,
        promo_seller_restricted: false,
        promo_allowed_seller_ids: [],
        visible_in_pos: true,
        visible_in_catalog: true,
        is_new_arrival: false,
        product_info: '',
        purchase_cost: ''
      });
      setVariants([]);
      setCurrentVariant({
        variant_name: '',
        sku: '',
        precio_1: '',
        precio_2: '',
        precio_3: '',
        precio_4: '',
        precio_5: '',
        mayoreo: '',
        medio_mayoreo: '',
        stock: '0',
        default_commission: '0',
        image_url: '',
        extra_images: []
      });
    }
    setEditingVariantIndex(null);
    setShowDialog(true);
  };

  // Deep-link: soporta ?edit=<id>&tab=promo desde Inventario y otros lugares
  useEffect(() => {
    if (!products || products.length === 0) return;
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (!editId) return;
    const p = products.find(x => x.id === editId);
    if (!p) return;
    handleOpenDialog(p);
    if (params.get('tab') === 'promo') {
      setTimeout(() => setPromoDialog(true), 200);
    }
    // Limpiar el query param para no reabrir en renders futuros
    navigate('/products', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, products.length]);

  // Modo embebido (/edit): abre la misma ventana sin mostrar la página
  const [historyProduct, setHistoryProduct] = useState(null);
  const [profitProduct, setProfitProduct] = useState(null);
  const [dormantOpen, setDormantOpen] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);
  const [bulkCostOpen, setBulkCostOpen] = useState(false);
  const embedOpenedRef = React.useRef(false);
  useEffect(() => {
    if (!embedded || embedOpenedRef.current) return;
    if (!embedEditId) { embedOpenedRef.current = true; handleOpenDialog(); return; }
    const p = products.find(x => x.id === embedEditId);
    if (p) { embedOpenedRef.current = true; handleOpenDialog(p); return; }
    if (products.length === 0) return;
    embedOpenedRef.current = true;
    axios.get(`${API}/products/full/${embedEditId}`, { withCredentials: true })
      .then(({ data }) => handleOpenDialog(data))
      .catch(() => { toast.error('No se pudo cargar el producto'); onEmbedClose?.(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, embedEditId, products.length]);

  const embedWasOpenRef = React.useRef(false);
  useEffect(() => {
    if (!embedded) return;
    if (showDialog) { embedWasOpenRef.current = true; return; }
    if (embedWasOpenRef.current) onEmbedClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog]);

  const handlePriceEdit = (product) => {
    // Admin y clerk abren el dialog directo. Clerk solicita aprobación al guardar.
    handleOpenDialog(product);
  };

  const handleDelete = (product) => {
    if (!isAdmin) {
      toast.error('Sólo el administrador puede eliminar productos.');
      return;
    }
    if (!window.confirm(`¿Eliminar "${product.name}"? Esta acción no se puede deshacer.`)) return;
    axios.delete(`${API}/products/${product.id}`, { withCredentials: true })
      .then(() => { toast.success('Producto eliminado'); refreshProducts(); })
      .catch(() => toast.error('Error al eliminar producto'));
  };

  const handleDeleteVariant = (variant) => {
    if (!isAdmin) {
      toast.error('Sólo el administrador puede eliminar variantes.');
      return;
    }
    if (!window.confirm(`¿Eliminar variante "${variant.variant_name || variant.name}"?`)) return;
    axios.delete(`${API}/products/${variant.id}`, { withCredentials: true })
      .then(() => { toast.success('Variante eliminada'); refreshProducts(); })
      .catch(() => toast.error('Error al eliminar variante'));
  };

  const handlePinSuccess = () => {
    if (pendingAction?.type === 'edit') {
      handleOpenDialog(pendingAction.product);
    } else if (pendingAction?.type === 'delete') {
      performDelete(pendingAction.product.id);
    } else if (pendingAction?.type === 'delete-variant') {
      performDeleteVariant(pendingAction.product.id);
    }
    setPendingAction(null);
  };

  const performDelete = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const performDeleteVariant = async (variantId) => {
    try {
      await axios.delete(`${API}/products/${variantId}`, { withCredentials: true });
      toast.success('Variante eliminada');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar variante');
    }
  };

  const addVariant = () => {
    if (!currentVariant.variant_name || !currentVariant.sku) {
      toast.error('Nombre de variante y SKU son requeridos');
      return;
    }

    if (editingVariantIndex !== null) {
      // Modo edición: reemplazar la variante existente preservando su id
      const newVariants = [...variants];
      newVariants[editingVariantIndex] = {
        ...newVariants[editingVariantIndex],
        ...currentVariant
      };
      setVariants(newVariants);
      setEditingVariantIndex(null);
      toast.success('Variante actualizada');
    } else {
      setVariants([...variants, { ...currentVariant }]);
      toast.success('Variante agregada');
    }

    setCurrentVariant({
      variant_name: '',
      sku: '',
      precio_1: formData.precio_1,
      precio_2: formData.precio_2,
      precio_3: formData.precio_3,
      precio_4: formData.precio_4,
      precio_5: formData.precio_5,
      mayoreo: formData.mayoreo,
      medio_mayoreo: formData.medio_mayoreo,
      stock: '0',
      default_commission: formData.default_commission,
      image_url: '',
      extra_images: []
    });
  };

  const editVariant = (index) => {
    const v = variants[index];
    setCurrentVariant({
      variant_name: v.variant_name || '',
      sku: v.sku || '',
      precio_1: v.precio_1 || '',
      precio_2: v.precio_2 || '',
      precio_3: v.precio_3 || '',
      precio_4: v.precio_4 || '',
      precio_5: v.precio_5 || '',
      mayoreo: v.mayoreo || '',
      medio_mayoreo: v.medio_mayoreo || '',
      stock: v.stock || '0',
      default_commission: v.default_commission || '0',
      image_url: v.image_url || '',
      extra_images: v.extra_images || []
    });
    setEditingVariantIndex(index);
  };

  const cancelEditVariant = () => {
    setEditingVariantIndex(null);
    setCurrentVariant({
      variant_name: '',
      sku: '',
      precio_1: formData.precio_1,
      precio_2: formData.precio_2,
      precio_3: formData.precio_3,
      precio_4: formData.precio_4,
      precio_5: formData.precio_5,
      mayoreo: formData.mayoreo,
      medio_mayoreo: formData.medio_mayoreo,
      stock: '0',
      default_commission: formData.default_commission,
      image_url: '',
      extra_images: []
    });
  };

  const removeVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
    if (editingVariantIndex === index) setEditingVariantIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        precio_1: parseFloat(formData.precio_1),
        precio_2: parseFloat(formData.precio_2),
        precio_3: parseFloat(formData.precio_3),
        precio_4: parseFloat(formData.precio_4),
        precio_5: parseFloat(formData.precio_5),
        mayoreo: parseFloat(formData.mayoreo),
        medio_mayoreo: parseFloat(formData.medio_mayoreo),
        stock: parseInt(formData.stock),
        min_stock: parseInt(formData.min_stock),
        default_commission: parseFloat(formData.default_commission),
        has_variants: variants.length > 0,
        category_id: formData.category_id === 'none' ? null : formData.category_id,
        has_promo: !!formData.has_promo,
        promo_type: formData.has_promo ? formData.promo_type : null,
        promo_price: formData.has_promo && formData.promo_type !== 'free_shipping' && formData.promo_price !== '' ? parseFloat(formData.promo_price) : null,
        promo_min_qty: formData.has_promo && formData.promo_type === 'min_qty' && formData.promo_min_qty !== ''
          ? parseInt(formData.promo_min_qty) : null,
        promo_end_date: formData.has_promo && formData.promo_end_date ? formData.promo_end_date : null,
        promo_local_only: formData.has_promo ? !!formData.promo_local_only : false,
        promo_shipment_only: formData.has_promo ? !!formData.promo_shipment_only : false,
        promo_discount_percent: formData.has_promo && formData.promo_type === 'percent_discount' && formData.promo_discount_percent !== ''
          ? parseFloat(formData.promo_discount_percent) : null,
        promo_qty_limit: formData.has_promo && formData.promo_type === 'limited_qty' && formData.promo_qty_limit !== ''
          ? parseInt(formData.promo_qty_limit) : null,
        promo_gift_product_id: formData.has_promo && formData.promo_type === 'gift' ? (formData.promo_gift_product_id || null) : null,
        promo_gift_qty: formData.has_promo && formData.promo_type === 'gift' ? Math.max(1, parseInt(formData.promo_gift_qty) || 1) : null,
        promo_combo_product_id: formData.has_promo && formData.promo_type === 'combo' ? (formData.promo_combo_product_id || null) : null,
        promo_combo_discount: formData.has_promo && formData.promo_type === 'combo' && formData.promo_combo_discount !== '' ? parseFloat(formData.promo_combo_discount) : null,
        promo_description: formData.has_promo ? (formData.promo_description || null) : null,
        promo_seller_restricted: formData.has_promo ? !!formData.promo_seller_restricted : false,
        promo_allowed_seller_ids: (formData.has_promo && formData.promo_seller_restricted) ? formData.promo_allowed_seller_ids : [],
        visible_in_pos: formData.visible_in_pos !== false,
        visible_in_catalog: formData.visible_in_catalog !== false,
        is_new_arrival: !!formData.is_new_arrival,
        product_info: (formData.product_info || '').trim() || null,
        purchase_cost: (formData.purchase_cost === '' || formData.purchase_cost === null || formData.purchase_cost === undefined)
          ? null
          : parseFloat(formData.purchase_cost)
      };

      let parentProductId;
      
      if (editingProduct) {
        // Si NO es admin → pedir aprobación del producto padre y de cada variante nueva.
        if (!isAdmin) {
          // 1) Detectar cambios reales en el producto padre vs. el original
          const parentFields = [
            'name','sku','brand','precio_1','precio_2','precio_3','precio_4','precio_5',
            'mayoreo','medio_mayoreo','stock','min_stock','default_commission',
            'image_url','category_id','has_promo','promo_type','promo_price',
            'promo_min_qty','promo_end_date','promo_local_only','promo_shipment_only',
            'promo_discount_percent','promo_qty_limit','promo_gift_product_id','promo_gift_qty',
            'promo_combo_product_id','promo_combo_discount','promo_description',
            'promo_seller_restricted','promo_allowed_seller_ids','visible_in_pos','visible_in_catalog'
          ];
          const parentChanged = parentFields.some(f => {
            const a = productData[f];
            const b = editingProduct[f];
            return (a ?? null) !== (b ?? null);
          });

          // 2) Detectar variantes nuevas (sin id)
          const newVariants = variants.filter(v => !v.id);

          if (!parentChanged && newVariants.length === 0) {
            toast.info('No hay cambios para enviar.');
            setShowDialog(false);
            setLoading(false);
            return;
          }

          const approvalIds = [];

          // a) Aprobación del producto padre (sólo si cambió). El flag has_variants
          //    se actualizará automáticamente en el backend al aprobar la variante.
          if (parentChanged) {
            const updatePayload = { ...productData };
            updatePayload.has_variants = editingProduct.has_variants || newVariants.length > 0;
            const { data } = await axios.post(
              `${API}/approvals/product-change`,
              { product_id: editingProduct.id, update_data: updatePayload },
              { withCredentials: true }
            );
            if (data.status === 'pending') approvalIds.push(data.id);
          }

          // b) Una aprobación product-create por cada variante nueva
          for (const variant of newVariants) {
            const variantData = {
              name: formData.name,
              sku: variant.sku,
              precio_1: parseFloat(variant.precio_1 || 0),
              precio_2: parseFloat(variant.precio_2 || 0),
              precio_3: parseFloat(variant.precio_3 || 0),
              precio_4: parseFloat(variant.precio_4 || 0),
              precio_5: parseFloat(variant.precio_5 || 0),
              mayoreo: parseFloat(variant.mayoreo || 0),
              medio_mayoreo: parseFloat(variant.medio_mayoreo || 0),
              stock: parseInt(variant.stock || 0),
              min_stock: parseInt(formData.min_stock || 0),
              default_commission: parseFloat(variant.default_commission || 0),
              image_url: variant.image_url || '',
              extra_images: variant.extra_images || [],
              parent_product_id: editingProduct.id,
              variant_name: variant.variant_name,
              has_variants: false,
              brand: formData.brand || null,
              category_id: formData.category_id === 'none' ? null : formData.category_id
            };
            try {
              const { data } = await axios.post(
                `${API}/approvals/product-create`,
                { create_data: variantData },
                { withCredentials: true }
              );
              if (data.status === 'pending') approvalIds.push(data.id);
            } catch (err) {
              toast.error(`Variante "${variant.variant_name}": ${err.response?.data?.detail || err.message}`);
            }
          }

          if (approvalIds.length > 0) {
            // Sólo polleamos el último ID (cuando se apruebe haremos refresh)
            setPendingApprovalId(approvalIds[approvalIds.length - 1]);
            const partes = [];
            if (parentChanged) partes.push('cambios del producto');
            if (newVariants.length > 0) partes.push(`${newVariants.length} variante(s) nueva(s)`);
            toast.info(`⏳ Enviado al admin para aprobación: ${partes.join(' y ')}.`);
          } else {
            toast.success('Producto actualizado');
            refreshProducts();
          }
          setShowDialog(false);
          setLoading(false);
          return;
        }

        // Actualizar producto existente (admin)
        await axios.put(`${API}/products/${editingProduct.id}`, productData, { withCredentials: true });
        parentProductId = editingProduct.id;
        
        // Si el producto tenía variantes antes, obtener sus IDs
        const existingVariants = products.filter(p => p.parent_product_id === editingProduct.id);
        const existingVariantIds = existingVariants.map(v => v.id);
        const updatedVariantIds = variants.filter(v => v.id).map(v => v.id);
        
        // Eliminar variantes que ya no están en la lista
        const variantsToDelete = existingVariantIds.filter(id => !updatedVariantIds.includes(id));
        for (const variantId of variantsToDelete) {
          await axios.delete(`${API}/products/${variantId}`, { withCredentials: true });
        }
        
        toast.success('Producto actualizado');
      } else {
        if (!isAdmin) {
          // Clerk: pedir aprobación para crear (con variantes embebidas si las hay)
          const createPayload = { ...productData };
          if (variants.length > 0) {
            createPayload.pending_variants = variants.map((v) => ({
              name: formData.name,
              sku: v.sku,
              precio_1: parseFloat(v.precio_1 || 0),
              precio_2: parseFloat(v.precio_2 || 0),
              precio_3: parseFloat(v.precio_3 || 0),
              precio_4: parseFloat(v.precio_4 || 0),
              precio_5: parseFloat(v.precio_5 || 0),
              mayoreo: parseFloat(v.mayoreo || 0),
              medio_mayoreo: parseFloat(v.medio_mayoreo || 0),
              stock: parseInt(v.stock || 0),
              min_stock: parseInt(formData.min_stock || 0),
              default_commission: parseFloat(v.default_commission || 0),
              image_url: v.image_url || '',
              extra_images: v.extra_images || [],
              variant_name: v.variant_name,
              brand: formData.brand || null,
              category_id: formData.category_id === 'none' ? null : formData.category_id
            }));
            createPayload.has_variants = true;
          }
          const { data } = await axios.post(
            `${API}/approvals/product-create`,
            { create_data: createPayload },
            { withCredentials: true }
          );
          if (data.status === 'approved') {
            const extra = data.variants_created ? ` con ${data.variants_created} variante(s)` : '';
            toast.success(`Producto creado${extra}`);
            refreshProducts();
          } else {
            setPendingApprovalId(data.id);
            const tag = variants.length > 0
              ? `⏳ Producto + ${variants.length} variante(s) enviados al admin para aprobación.`
              : '⏳ Producto enviado al admin para aprobación.';
            toast.info(tag);
          }
          setShowDialog(false);
          setLoading(false);
          return;
        }
        // Crear nuevo producto (admin)
        const response = await axios.post(`${API}/products`, productData, { withCredentials: true });
        parentProductId = response.data.id;
        toast.success('Producto creado');
      }

      // Crear o actualizar variantes
      if (variants.length > 0) {
        for (const variant of variants) {
          const variantData = {
            name: formData.name,
            sku: variant.sku,
            precio_1: parseFloat(variant.precio_1 || 0),
            precio_2: parseFloat(variant.precio_2 || 0),
            precio_3: parseFloat(variant.precio_3 || 0),
            precio_4: parseFloat(variant.precio_4 || 0),
            precio_5: parseFloat(variant.precio_5 || 0),
            mayoreo: parseFloat(variant.mayoreo || 0),
            medio_mayoreo: parseFloat(variant.medio_mayoreo || 0),
            stock: parseInt(variant.stock || 0),
            min_stock: parseInt(formData.min_stock),
            default_commission: parseFloat(variant.default_commission || 0),
            image_url: variant.image_url,
            extra_images: variant.extra_images || [],
            parent_product_id: parentProductId,
            variant_name: variant.variant_name,
            has_variants: false,
            brand: formData.brand || null,
            category_id: formData.category_id === 'none' ? null : formData.category_id
          };

          if (variant.id) {
            // Actualizar variante existente
            await axios.put(`${API}/products/${variant.id}`, variantData, { withCredentials: true });
          } else {
            // Crear nueva variante
            await axios.post(`${API}/products`, variantData, { withCredentials: true });
          }
        }
      }

      setShowDialog(false);
      setVariants([]);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error al guardar producto: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleVariants = (productId) => {
    setShowVariants({ ...showVariants, [productId]: !showVariants[productId] });
  };

  const parentProducts = products.filter(p => !p.parent_product_id);
  const filteredParentProducts = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return parentProducts;
    return parentProducts.filter(p => {
      // Coincide en el producto padre por nombre, SKU o marca
      if ((p.name || '').toLowerCase().includes(q)) return true;
      if ((p.sku || '').toLowerCase().includes(q)) return true;
      if ((p.brand || '').toLowerCase().includes(q)) return true;
      // O en alguna de sus variantes
      const childVariants = products.filter(v => v.parent_product_id === p.id);
      return childVariants.some(v =>
        (v.name || '').toLowerCase().includes(q) ||
        (v.variant_name || '').toLowerCase().includes(q) ||
        (v.sku || '').toLowerCase().includes(q)
      );
    });
  })();

  if (loading && !embedded) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p>Cargando...</p>
        </div>
      </MainLayout>
    );
  }

  const PageWrap = embedded ? React.Fragment : MainLayout;
  return (
    <PageWrap>
      {!embedded && (
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Productos</h1>
            <p className="text-muted-foreground mt-1">Gestiona tu catálogo de productos</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={() => setDormantOpen(true)} data-testid="dormant-products-button">😴 Dormidos</Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setRankingOpen(true)} data-testid="profit-ranking-button">🏆 Ranking ganancia</Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setBulkCostOpen(true)} data-testid="bulk-cost-button">💲 Costos</Button>
            )}
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={importExportLoading}
              data-testid="export-products-button"
            >
              <Download size={18} className="mr-2" />
              Exportar Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById('import-excel-input').click()}
              disabled={importExportLoading}
              data-testid="import-products-button"
            >
              <Upload size={18} className="mr-2" />
              Importar Excel
            </Button>
            <input
              id="import-excel-input"
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleImport}
            />
            <Button onClick={() => handleOpenDialog()} data-testid="add-product-button">
              <Plus size={20} weight="bold" className="mr-2" />
              Nuevo Producto
            </Button>
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm">
          <div className="px-4 py-3 border-b border-border bg-slate-50/40">
            <div className="relative max-w-md">
              <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre, SKU, marca o variante…"
                className="pl-10 pr-10"
                data-testid="products-search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="products-search-clear"
                  title="Limpiar"
                >
                  <X size={16} weight="bold" />
                </button>
              )}
            </div>
            {searchTerm && (
              <p className="text-xs text-muted-foreground mt-1">
                {filteredParentProducts.length} de {parentProducts.length} productos
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead className="bg-slate-50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Imagen</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">SKU</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">P1</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Mayoreo</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Stock</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Comisión</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredParentProducts.map((product) => {
                  const productVariants = products.filter(p => p.parent_product_id === product.id);
                  const hasVariants = productVariants.length > 0;
                  const isHidden = product.visible_in_pos === false;

                  return (
                    <React.Fragment key={product.id}>
                      <tr
                        className={`hover:bg-slate-50 btn-transition ${isHidden ? 'bg-amber-50/40' : ''}`}
                        data-testid={`product-row-${product.id}`}
                      >
                        <td className="px-4 py-2">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className={`w-12 h-12 object-cover rounded ${isHidden ? 'opacity-50 grayscale' : ''}`} />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                              <ImageIcon size={24} className="text-slate-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={isHidden ? 'text-slate-500' : ''}>{product.name}</span>
                            {isAdmin && <MissingInfoBadges product={product} />}
                            {isAdmin && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setProfitProduct({ id: product.id, name: product.name }); }}
                                className="text-xs px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                                title="Ver ganancia y estadísticas" data-testid={`profit-stats-btn-${product.id}`}>💹</button>
                            )}
                            {isAdmin && !product.has_variants && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setHistoryProduct({ id: product.id, name: product.name }); }}
                                className="text-xs px-1.5 py-0.5 rounded border border-slate-300 hover:bg-sky-50 hover:border-sky-400"
                                title="Historial de movimientos de stock" data-testid={`stock-history-btn-${product.id}`}>📜</button>
                            )}
                            {isHidden && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded"
                                title="Este producto no aparece en el POS"
                                data-testid={`hidden-badge-${product.id}`}
                              >
                                <EyeSlash size={10} weight="fill" /> Oculto POS
                              </span>
                            )}
                            {hasVariants && (
                              <button
                                onClick={() => toggleVariants(product.id)}
                                className="text-xs text-primary hover:underline"
                              >
                                ({productVariants.length} variantes)
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm">{product.sku}</td>
                        <td className="px-4 py-2 text-sm text-right">${(Number(product.precio_1) || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right">${(Number(product.mayoreo) || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right">
                          {(() => {
                            const stock = product.has_variants
                              ? products.filter(p => p.parent_product_id === product.id).reduce((acc, v) => acc + (parseInt(v.stock) || 0), 0)
                              : (parseInt(product.stock) || 0);
                            const cls = stock === 0
                              ? 'bg-rose-100 text-rose-700 font-bold px-2 py-0.5 rounded'
                              : stock <= (product.min_stock || 0)
                                ? 'bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded'
                                : '';
                            return (
                              <span
                                className={cls}
                                title={product.has_variants ? 'Suma de stock de variantes' : (stock === 0 ? 'AGOTADO en esta sucursal' : '')}
                                data-testid={`product-stock-${product.id}`}
                              >
                                {stock}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">${(product.default_commission || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePriceEdit(product)}
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(product)}
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {showVariants[product.id] && productVariants.map((variant) => {
                        const vHidden = variant.visible_in_pos === false;
                        return (
                        <tr key={variant.id} className={`bg-slate-50/50 ${vHidden ? 'bg-amber-50/60' : ''}`}>
                          <td className="px-4 py-2 pl-8">
                            {variant.image_url ? (
                              <img src={variant.image_url} alt={variant.variant_name} className={`w-10 h-10 object-cover rounded ${vHidden ? 'opacity-50 grayscale' : ''}`} />
                            ) : (
                              <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                <ImageIcon size={20} className="text-slate-400" />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 pl-8 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={vHidden ? 'text-slate-400' : ''}>↳ {variant.variant_name}</span>
                              {isAdmin && !variant.has_variants && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setHistoryProduct({ id: variant.id, name: `${product.name} (${variant.variant_name})` }); }}
                                className="text-xs px-1.5 py-0.5 rounded border border-slate-300 hover:bg-sky-50 hover:border-sky-400"
                                title="Historial de movimientos de stock" data-testid={`stock-history-btn-${variant.id}`}>📜</button>
                            )}
                              {vHidden && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded"
                                  title="Esta variante no aparece en el POS"
                                  data-testid={`hidden-badge-variant-${variant.id}`}
                                >
                                  <EyeSlash size={10} weight="fill" /> Oculto POS
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">{variant.sku}</td>
                          <td className="px-4 py-2 text-sm text-right">${(Number(variant.precio_1) || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm text-right">${(Number(variant.mayoreo) || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm text-right">{variant.stock}</td>
                          <td className="px-4 py-2 text-sm text-right">${(variant.default_commission || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePriceEdit(product)}
                                data-testid={`edit-variant-${variant.id}`}
                                title="Editar variantes (abre el producto padre)"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteVariant(variant)}
                                data-testid={`delete-variant-${variant.id}`}
                                title="Eliminar esta variante"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {parentProducts.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                      No hay productos registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {!embedded && <StockHistoryDialog product={historyProduct} onClose={() => setHistoryProduct(null)} />}
      {!embedded && <ProfitStatsDialog product={profitProduct} onClose={() => setProfitProduct(null)} />}
      {!embedded && <BulkCostDialog open={bulkCostOpen} onClose={() => setBulkCostOpen(false)} onSaved={fetchProducts} />}
      {!embedded && <ProfitRankingDialog open={rankingOpen} onClose={() => setRankingOpen(false)} />}
      {!embedded && <DormantProductsDialog open={dormantOpen} onClose={() => setDormantOpen(false)} onPromo={(it) => {
        const p = products.find(x => x.id === it.id && !x.parent_product_id) || products.find(x => x.id === (it.parent_product_id || it.id));
        if (!p) return toast.error('No se encontró el producto');
        setDormantOpen(false);
        handleOpenDialog(p);
        setTimeout(() => setPromoDialog(true), 200);
      }} />}

      {/* Product Form Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="product-form">
            <Tabs defaultValue="general">
              <TabsList>
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="precios">Precios</TabsTrigger>
                <TabsTrigger value="variantes">Variantes ({variants.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Imagen del Producto</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {formData.image_url ? (
                        <div className="relative">
                          <img src={formData.image_url} alt="Preview" className="w-24 h-24 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, image_url: '' })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="w-24 h-24 bg-slate-100 rounded border flex items-center justify-center">
                          <ImageIcon size={32} className="text-slate-400" />
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*,.heic,.heif"
                        onChange={(e) => handleImageUpload(e, false)}
                        className="max-w-xs"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label>Imágenes adicionales (galería)</Label>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      {(formData.extra_images || []).map((img, idx) => (
                        <div key={idx} className="relative">
                          <img src={img} alt={`extra-${idx}`} className="w-20 h-20 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              extra_images: formData.extra_images.filter((_, i) => i !== idx)
                            })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                            data-testid={`remove-extra-image-${idx}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:border-violet-500 hover:bg-violet-50">
                        <input
                          type="file"
                          accept="image/*,.heic,.heif"
                          multiple
                          className="hidden"
                          data-testid="extra-images-upload"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            for (const file of files) {
                              try {
                                const dataUrl = await compressImage(file);
                                setFormData(prev => ({
                                  ...prev,
                                  extra_images: [...(prev.extra_images || []), dataUrl]
                                }));
                              } catch (err) {
                                toast.error(`${file.name}: ${err.message || 'no se pudo procesar'}`);
                              }
                            }
                            e.target.value = '';
                          }}
                        />
                        <span className="text-2xl text-slate-400">+</span>
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      💡 Estas imágenes se enviarán como álbum cuando un cliente consulte el producto por Telegram. Máx 10.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="name">Nombre del Producto *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="product-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="sku">SKU *</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      data-testid="product-sku-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="brand">Marca</Label>
                    <Input
                      id="brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      data-testid="product-brand-input"
                      placeholder="Ej: Nike, Adidas, Coppel..."
                      list="brand-suggestions"
                    />
                    <datalist id="brand-suggestions">
                      {Array.from(new Set(products.map(p => p.brand).filter(Boolean))).sort().map((b) => (
                        <option key={b} value={b} />
                      ))}
                    </datalist>
                  </div>
                  <div className="col-span-2">
                    <Label>Categoría</Label>
                    <CategoryCombobox
                      value={formData.category_id}
                      onChange={(id) => setFormData({ ...formData, category_id: id })}
                      categories={categories}
                      onCategoriesChange={async () => {
                        await refreshCategories();
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Busca en la lista o escribe un nombre nuevo para crear la categoría al vuelo.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock</Label>
                    <Input
                      id="stock"
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      data-testid="product-stock-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="min_stock">Stock Mínimo</Label>
                    <Input
                      id="min_stock"
                      type="number"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      data-testid="product-min-stock-input"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="default_commission">Comisión por Defecto ($)</Label>
                    <Input
                      id="default_commission"
                      type="number"
                      step="0.01"
                      value={formData.default_commission}
                      onChange={(e) => setFormData({ ...formData, default_commission: e.target.value })}
                      data-testid="product-commission-input"
                    />
                  </div>

                  {/* Disponibilidad en POS */}
                  <div className="col-span-2 bg-slate-50 border border-border rounded p-3 flex items-center justify-between">
                    <div>
                      <Label className="font-semibold">Disponible en Punto de Venta</Label>
                      <p className="text-xs text-slate-500 mt-1">Si lo apagas, el producto y sus variantes NO aparecerán en el POS. Sigue editable desde aquí.</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.visible_in_pos !== false}
                      onClick={() => setFormData({ ...formData, visible_in_pos: !(formData.visible_in_pos !== false) })}
                      data-testid="product-visible-in-pos"
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent btn-transition ${
                        formData.visible_in_pos !== false ? 'bg-emerald-500' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 btn-transition ${
                        formData.visible_in_pos !== false ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>

                  {/* Visibilidad en Catálogo público y Ofertas */}
                  <div className="col-span-2 bg-slate-50 border border-border rounded p-3 flex items-center justify-between">
                    <div>
                      <Label className="font-semibold">Visible en Catálogo y Ofertas</Label>
                      <p className="text-xs text-slate-500 mt-1">Si lo apagas, este producto NO aparecerá en las páginas públicas de <b>catálogo</b> ni de <b>ofertas</b>. Sigue disponible en el POS (a menos que también apagues el switch de arriba).</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.visible_in_catalog !== false}
                      onClick={() => setFormData({ ...formData, visible_in_catalog: !(formData.visible_in_catalog !== false) })}
                      data-testid="product-visible-in-catalog"
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent btn-transition ${
                        formData.visible_in_catalog !== false ? 'bg-emerald-500' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 btn-transition ${
                        formData.visible_in_catalog !== false ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>

                  {/* Producto NUEVO - siempre visible (al crear inicia el contador; al editar lo reinicia) */}
                  <div className="col-span-2 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg" role="img" aria-label="nuevo">🆕</span>
                        <Label htmlFor="is_new_arrival" className="text-sm font-semibold cursor-pointer">
                          Marcar como <span className="text-emerald-700">PRODUCTO NUEVO</span>
                          <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                            {editingProduct
                              ? 'Al guardar reinicia los 10 días con la etiqueta "Nuevo" en catálogo y ofertas'
                              : 'Aparece con etiqueta "Nuevo" en catálogo y ofertas durante 10 días'}
                          </span>
                        </Label>
                      </div>
                      <input
                        id="is_new_arrival"
                        type="checkbox"
                        checked={!!formData.is_new_arrival}
                        onChange={(e) => setFormData({ ...formData, is_new_arrival: e.target.checked })}
                        data-testid="product-new-arrival-checkbox"
                        className="h-5 w-5 cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Información del producto (visible siempre) */}
                  <div className="col-span-2 border-t pt-4">
                    <Label htmlFor="product_info" className="text-sm font-semibold flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold">i</span>
                      Información del producto
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                      Detalles como medidas, material, uso, garantía. Aparece al tocar el ícono "i" en catálogo y ofertas.
                    </p>
                    <textarea
                      id="product_info"
                      value={formData.product_info || ''}
                      onChange={(e) => setFormData({ ...formData, product_info: e.target.value })}
                      placeholder="Ejemplo: Silla de plástico resistente 90x45cm, soporta 120 kg, disponible en negro y blanco…"
                      rows={3}
                      maxLength={800}
                      data-testid="product-info-textarea"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500 resize-y"
                    />
                    <div className="text-[10px] text-slate-400 text-right mt-0.5">
                      {(formData.product_info || '').length} / 800
                    </div>

                    {/* Costo de compra (informativo, camuflado) */}
                    <div className="mt-3 flex items-center gap-2">
                      <Label htmlFor="purchase_cost" className="text-[11px] text-slate-400 shrink-0">
                        Costo de compra (informativo)
                      </Label>
                      <input
                        id="purchase_cost"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.purchase_cost ?? ''}
                        onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })}
                        placeholder="$ 0.00"
                        data-testid="product-purchase-cost-input"
                        className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-md text-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300 focus:border-slate-400"
                      />
                    </div>
                  </div>

                  {/* Promotion */}
                  <div className="col-span-2 border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Tag size={18} className="text-emerald-600" />
                        <Label htmlFor="has_promo" className="text-sm font-semibold cursor-pointer">
                          ¿Tiene promoción?
                        </Label>
                      </div>
                      <input
                        id="has_promo"
                        type="checkbox"
                        checked={formData.has_promo}
                        onChange={(e) => {
                          setFormData({ ...formData, has_promo: e.target.checked });
                          if (e.target.checked) setPromoDialog(true);
                        }}
                        data-testid="product-has-promo-checkbox"
                        className="h-5 w-5 cursor-pointer"
                      />
                    </div>
                    {formData.has_promo && (
                      <div className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm space-y-1">
                        <p>
                          {formData.promo_type === 'flat' && <>🏷️ Precio fijo: <strong>${formData.promo_price || '?'}</strong></>}
                          {formData.promo_type === 'min_qty' && <>📦 <strong>${formData.promo_price || '?'}</strong> si lleva ≥ <strong>{formData.promo_min_qty || '?'}</strong> pzas</>}
                          {formData.promo_type === 'percent_discount' && <>% Descuento: <strong>{formData.promo_discount_percent || '?'}%</strong></>}
                          {formData.promo_type === 'limited_qty' && <>🔢 <strong>${formData.promo_price || '?'}</strong> · Cupo <strong>{formData.promo_qty_sold || 0}/{formData.promo_qty_limit || '?'}</strong></>}
                          {formData.promo_type === 'gift' && <>🎁 Regalo: <strong>{(products || []).find(p => p.id === formData.promo_gift_product_id)?.name || '?'}</strong> (×{formData.promo_gift_qty || 1})</>}
                          {formData.promo_type === 'free_shipping' && <>🚚 <strong>Envío gratis</strong> en envíos con este producto</>}
                          {formData.promo_type === 'combo' && <>🔗 -<strong>${formData.promo_combo_discount || '?'}</strong> si va con <strong>{(products || []).find(p => p.id === formData.promo_combo_product_id)?.name || '?'}</strong></>}
                        </p>
                        {formData.promo_seller_restricted && (
                          <p className="text-xs">👤 Solo vendedores: <strong>{(formData.promo_allowed_seller_ids || []).length}</strong></p>
                        )}
                        {formData.promo_local_only && <p className="text-xs">🏪 Solo local</p>}
                        {formData.promo_shipment_only && <p className="text-xs">🚚 Solo envío</p>}
                        {formData.promo_end_date && (
                          <p className="text-xs">📅 Vence: {formData.promo_end_date}</p>
                        )}
                        <Button type="button" size="sm" variant="outline" onClick={() => setPromoDialog(true)} data-testid="edit-promo-button">
                          Editar promoción
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="precios" className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="precio_1">Precio 1</Label>
                    <Input
                      id="precio_1"
                      type="number"
                      step="0.01"
                      value={formData.precio_1} placeholder="0.00"
                      onChange={(e) => setFormData({ ...formData, precio_1: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="precio_2">Precio 2</Label>
                    <Input
                      id="precio_2"
                      type="number"
                      step="0.01"
                      value={formData.precio_2} placeholder="0.00"
                      onChange={(e) => setFormData({ ...formData, precio_2: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="precio_3">Precio 3</Label>
                    <Input
                      id="precio_3"
                      type="number"
                      step="0.01"
                      value={formData.precio_3} placeholder="0.00"
                      onChange={(e) => setFormData({ ...formData, precio_3: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="precio_4">Precio 4</Label>
                    <Input
                      id="precio_4"
                      type="number"
                      step="0.01"
                      value={formData.precio_4} placeholder="0.00"
                      onChange={(e) => setFormData({ ...formData, precio_4: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="precio_5">Precio 5</Label>
                    <Input
                      id="precio_5"
                      type="number"
                      step="0.01"
                      value={formData.precio_5} placeholder="0.00"
                      onChange={(e) => setFormData({ ...formData, precio_5: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="mayoreo">Mayoreo</Label>
                    <Input
                      id="mayoreo"
                      type="number"
                      step="0.01"
                      value={formData.mayoreo}
                      onChange={(e) => setFormData({ ...formData, mayoreo: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="medio_mayoreo">Medio Mayoreo</Label>
                    <Input
                      id="medio_mayoreo"
                      type="number"
                      step="0.01"
                      value={formData.medio_mayoreo}
                      onChange={(e) => setFormData({ ...formData, medio_mayoreo: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="variantes" className="space-y-4">
                <div className="border border-border rounded p-4 space-y-4">
                  <h3 className="font-semibold">Nueva Variante</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label>Nombre Variante *</Label>
                      <Input
                        value={currentVariant.variant_name}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, variant_name: e.target.value })}
                        placeholder="Ej: Blanca"
                      />
                    </div>
                    <div>
                      <Label>SKU *</Label>
                      <Input
                        value={currentVariant.sku}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, sku: e.target.value })}
                        placeholder="SKU único"
                      />
                    </div>
                    <div>
                      <Label>Stock</Label>
                      <Input
                        type="number"
                        value={currentVariant.stock}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, stock: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Comisión ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.default_commission}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, default_commission: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label>Precio 1</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.precio_1}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, precio_1: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Precio 2</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.precio_2}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, precio_2: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Precio 3</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.precio_3}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, precio_3: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Precio 4</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.precio_4}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, precio_4: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Precio 5</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.precio_5}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, precio_5: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Mayoreo</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.mayoreo}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, mayoreo: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>M. Mayoreo</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentVariant.medio_mayoreo}
                        onChange={(e) => setCurrentVariant({ ...currentVariant, medio_mayoreo: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Imagen de Variante</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {currentVariant.image_url ? (
                        <div className="relative">
                          <img src={currentVariant.image_url} alt="Preview" className="w-20 h-20 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => setCurrentVariant({ ...currentVariant, image_url: '' })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="w-20 h-20 bg-slate-100 rounded border flex items-center justify-center">
                          <ImageIcon size={24} className="text-slate-400" />
                        </div>
                      )}
                      <Input
                        type="file"
                        accept="image/*,.heic,.heif"
                        onChange={(e) => handleImageUpload(e, true)}
                        className="max-w-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Imágenes adicionales (galería)</Label>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      {(currentVariant.extra_images || []).map((img, idx) => (
                        <div key={idx} className="relative">
                          <img src={img} alt={`v-extra-${idx}`} className="w-16 h-16 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => setCurrentVariant({
                              ...currentVariant,
                              extra_images: currentVariant.extra_images.filter((_, i) => i !== idx)
                            })}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                            data-testid={`remove-variant-extra-${idx}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:border-violet-500 hover:bg-violet-50">
                        <input
                          type="file"
                          accept="image/*,.heic,.heif"
                          multiple
                          className="hidden"
                          data-testid="variant-extra-images-upload"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            for (const file of files) {
                              try {
                                const dataUrl = await compressImage(file);
                                setCurrentVariant(prev => ({
                                  ...prev,
                                  extra_images: [...(prev.extra_images || []), dataUrl]
                                }));
                              } catch (err) {
                                toast.error(`${file.name}: ${err.message || 'no se pudo procesar'}`);
                              }
                            }
                            e.target.value = '';
                          }}
                        />
                        <span className="text-xl text-slate-400">+</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" onClick={addVariant} variant="outline" className="flex-1">
                      <Plus size={16} className="mr-2" />
                      {editingVariantIndex !== null ? 'Actualizar Variante' : 'Agregar Variante'}
                    </Button>
                    {editingVariantIndex !== null && (
                      <Button type="button" onClick={cancelEditVariant} variant="ghost">
                        Cancelar
                      </Button>
                    )}
                  </div>
                </div>

                {variants.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold">Variantes ({variants.length})</h3>
                    {variants.map((variant, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-4 p-3 border rounded ${
                          editingVariantIndex === index ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        {variant.image_url ? (
                          <img src={variant.image_url} alt={variant.variant_name} className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                            <ImageIcon size={20} className="text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{variant.variant_name}</p>
                          <p className="text-sm text-muted-foreground">
                            SKU: {variant.sku} | Stock: {variant.stock} | P1: ${variant.precio_1}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => editVariant(index)}
                          data-testid={`edit-variant-form-${index}`}
                          title="Editar esta variante"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => removeVariant(index)}
                          data-testid={`remove-variant-form-${index}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-product-button">
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-product-button">
                {editingProduct ? 'Actualizar' : 'Crear'} Producto
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PinVerificationDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onSuccess={handlePinSuccess}
        title="Verificación Requerida"
      />

      {/* Promo dialog */}
      <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} className="text-emerald-600" />
              Configurar Promoción
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <PromoTypeFields formData={formData} setFormData={setFormData} products={[...(products || []), ...(packages || []).map(pk => ({ ...pk, is_package: true }))]} selfId={editingProduct?.id} selfName={formData.name} />

            <div>
              <Label>Descripción (opcional)</Label>
              <Input value={formData.promo_description || ''} onChange={(e) => setFormData({ ...formData, promo_description: e.target.value })}
                placeholder="Ej. Promo verano" data-testid="promo-description-input" />
            </div>

            <div>
              <Label htmlFor="promo_end_date">Fecha de finalización (opcional)</Label>
              <Input
                id="promo_end_date"
                type="date"
                value={formData.promo_end_date}
                onChange={(e) => setFormData({ ...formData, promo_end_date: e.target.value })}
                data-testid="promo-end-date-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Después de esta fecha, la promo dejará de aplicarse automáticamente.
              </p>
            </div>

            <div>
              <Label className="mb-2 block">Alcance de la promoción</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, promo_local_only: false, promo_shipment_only: false })}
                  className={`p-2 border-2 rounded text-xs btn-transition ${
                    !formData.promo_local_only && !formData.promo_shipment_only
                      ? 'border-emerald-500 bg-emerald-50 font-semibold'
                      : 'border-border hover:bg-slate-50'
                  }`}
                  data-testid="promo-scope-both"
                >🏪🚚 Ambos</button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, promo_local_only: true, promo_shipment_only: false })}
                  className={`p-2 border-2 rounded text-xs btn-transition ${
                    formData.promo_local_only ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'border-border hover:bg-slate-50'
                  }`}
                  data-testid="promo-scope-local"
                >🏪 Solo local</button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, promo_local_only: false, promo_shipment_only: true })}
                  className={`p-2 border-2 rounded text-xs btn-transition ${
                    formData.promo_shipment_only ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'border-border hover:bg-slate-50'
                  }`}
                  data-testid="promo-scope-shipment"
                >🚚 Solo envío</button>
              </div>
            </div>

            <PromoSellerPicker formData={formData} setFormData={setFormData} sellers={sellers} />

            <div className="flex justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  setFormData({
                    ...formData, has_promo: false,
                    promo_price: '', promo_min_qty: '', promo_end_date: '',
                    promo_local_only: false, promo_shipment_only: false,
                    promo_discount_percent: '', promo_qty_limit: '',
                    promo_gift_product_id: '', promo_gift_qty: 1,
                    promo_combo_product_id: '', promo_combo_discount: '', promo_description: '',
                    promo_seller_restricted: false, promo_allowed_seller_ids: [],
                  });
                  setPromoDialog(false);
                }}
                data-testid="remove-promo-button"
              >
                Quitar promo
              </Button>
              <Button type="button" onClick={() => setPromoDialog(false)} data-testid="save-promo-button">
                Guardar configuración
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageWrap>
  );
};

// Add React import for Fragment
import React from 'react';
